import { useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import styles from './Leave.module.scss';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { Dropdown, borderedSelectStyles } from '@/components/Dropdown';
import { Input } from '@/components/Input';
import { Button, buttonVariants } from '@/components/Button';
import { addNotification, notificationStates } from '@components/Notification';
import { leaveBalanceService } from '@services/leaveBalance';

import {
  commonDateLabelTextProps,
  paidLeaveTypeList,
  paidLeaveTypeOptions,
  unpaidLeaveOption,
} from './Leave.constants';
import {
  getSelectedTableData,
  validateLeaveForm,
  canUseLeaveDateSelection,
  isLeaveDateSelectableInCalendar,
} from './Leave.helper';
import { useSelector } from 'react-redux';
import firebase from '@config/firebase.config';
const database = firebase.firestore();

export const UpdateLeaveFormModal = ({
  leaveData,
  companyManagers,
  onClose,
  fetchLeaveRequestsList,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { activeCompany } = useSelector((state) => state.auth.profile);
  const { profile } = useSelector((state) => state.auth);
  const teams = useSelector((state) => ({
    projects: state.team.projects,
    active: state.team.active,
    teams: state?.team?.teams,
  }));

  const selectedManager = useMemo(() => {
    const manager = companyManagers.find(
      (manager) => manager.userUID === leaveData.assignee
    );
    return {
      ...manager,
      label: manager?.userName,
      value: manager?.userUID,
    };
  }, [companyManagers, leaveData]);

  const [formData, setFormData] = useState({
    leaveType: {
      label: leaveData.type,
      value: leaveData.type,
    },
    leaveComment: leaveData.comment,
    leaveManager: selectedManager,
    leaveDate: (() => {
      const parts = leaveData.leaveDate?.split('-') || [];
      const y = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10);
      const d = parseInt(parts[2], 10);
      return {
        year: Number.isFinite(y) ? y : null,
        month: Number.isFinite(m) ? m : null,
        day: Number.isFinite(d) ? d : null,
      };
    })(),
  });
  const companyManagersDropdownOptions = useMemo(() => {
    return companyManagers.map((manager) => ({
      ...manager,
      label: manager.userName,
      value: manager.userUID,
    }));
  }, [companyManagers]);
  const approvedLeave = useMemo(() => {
    return leaveData.status === 'Approved';
  }, [leaveData]);
  const selectedTableData = useMemo(() => {
    return getSelectedTableData(leaveData, selectedManager);
  }, [leaveData]);

  const selectedLeaveDate = useMemo(() => {
    const ld = formData.leaveDate;
    if (!ld || ld.year == null || ld.month == null || ld.day == null) {
      return null;
    }
    const y = Number(ld.year);
    const m = Number(ld.month);
    const d = Number(ld.day);
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
      return null;
    }
    const date = new Date(y, m - 1, d);
    return Number.isNaN(date.getTime()) ? null : date;
  }, [formData.leaveDate]);

  const todayMin = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);
  const validateLeaveTypeChange = async (newType, originalType) => {
    try {
      // Use the service to check if the change is allowed
      const changeCheck = await leaveBalanceService.canChangeLeaveType(
        profile.id,
        activeCompany.companyId,
        newType,
        originalType
      );

      // If changing from Paid Leave to another type, check if it's already approved
      if (paidLeaveTypeList.includes(originalType)) {
        if (leaveData.status === 'Approved') {
          addNotification({
            message:
              'Cannot change leave type for an approved paid leave request. Please contact your administrator.',
            level: notificationStates.error,
          });
          return false;
        }
      }

      // If changing to Paid Leave, validate balance
      if (paidLeaveTypeList.includes(newType)) {
        if (!changeCheck.canChange) {
          addNotification({
            message: `Cannot change to Paid Leave: You have insufficient paid leave balance. You have ${
              changeCheck.remainingBalance || 0
            } days remaining.`,
            level: notificationStates.error,
          });
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error('Error validating leave type change:', error);
      addNotification({
        message: 'Error validating leave type change. Please try again.',
        level: notificationStates.error,
      });
      return false;
    }
  };
  const handleUpdate = async () => {
    if (isSubmitting) return; // Prevent multiple submissions

    setIsSubmitting(true); // Start loading

    if (!validateLeaveForm(formData)) {
      setIsSubmitting(false);
      return;
    }

    // Check if leave type is being changed and validate accordingly
    const newType = formData.leaveType.value || leaveData.type;
    const originalType = leaveData.type;

    // Validate leave type change
    if (!(await validateLeaveTypeChange(newType, originalType))) {
      setIsSubmitting(false);
      return;
    }

    const leaveDate = `${formData.leaveDate.year}-${formData.leaveDate.month}-${formData.leaveDate.day}`;

    const companyLeaveRef = database
      .collection('companyDatabase')
      .doc(activeCompany.companyId)
      .collection('leaveRequests');
    const query = companyLeaveRef
      .where('userId', '==', profile.id)
      .where('leaveDate', '==', leaveDate)
      .where('status', '!=', 'Declined');
    query.get().then(async (querySnap) => {
      let entryexist = false;
      if (!querySnap.empty) {
        querySnap.docs.forEach((doc) => {
          if (leaveData.id !== doc.id) {
            addNotification({
              message: `You already have submitted a request for leave on the date - ${leaveDate}`,
              level: notificationStates.error,
            });
            entryexist = true;
          }
        });
        if (entryexist) return;
      }

      const managerId = formData.leaveManager.value || leaveData.assignee;
      const finalData = {
        userId: profile.id,
        companyId: activeCompany.companyId,
        companyID: activeCompany.companyId,
        teamId: teams.active?.activeTeamid || leaveData.teamId || '',
        assignee: managerId,
        managerId,
        status: 'Pending',
        type: formData.leaveType.value || leaveData.type,
        comment: formData.leaveComment || leaveData.comment,
        leaveDate,
        requestDate: new Date(),
      };

      const ref = companyLeaveRef.doc(leaveData?.id);

      return ref
        .update(finalData)
        .then(async () => {
          addNotification({
            message: 'Request Updated successfully',
            level: notificationStates.success,
          });
          setIsSubmitting(false);
          onClose();
          fetchLeaveRequestsList();
        })
        .catch((error) => {
          console.error('Error updating leave request:', error);
          addNotification({
            message: 'Request update failed',
            level: notificationStates.error,
          });
          setIsSubmitting(false);
        });
    });
  };
  const handleDelete = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    const ref = database
      .collection('companyDatabase')
      .doc(activeCompany.companyId)
      .collection('leaveRequests')
      .doc(leaveData?.id);
    return ref
      .delete()
      .then(() => {
        addNotification({
          message: 'Request deleted successfully',
          level: notificationStates.success,
        });
        setIsDeleting(false);
        onClose();
        fetchLeaveRequestsList();
      })
      .catch((error) => {
        console.error('Error deleting leave request:', error);
        addNotification({
          message: 'Request deletion failed',
          level: notificationStates.error,
        });
        setIsDeleting(false);
      });
  };
  const filteredLeaveTypeOptions = useMemo(() => {
    let filteredOptions = [];
    if (paidLeaveTypeList.includes(formData.leaveType.value)) {
      filteredOptions = [...paidLeaveTypeOptions];
    } else {
      filteredOptions = [{ ...unpaidLeaveOption }];
    }
    return filteredOptions;
  });

  return (
    <div className={styles.updateLeaveFormModal}>
      <Text
        variant={textVariantsEnum.Text_md}
        fontWeight={fontWeightEnum.Medium}
      >
        Update Leave Form
      </Text>
      <div className={styles.updateLeaveFormModalContent}>
        <div className={styles.selectedDataContainer}>
          {selectedTableData.map((item) => (
            <div className={styles.selectedDataItem} key={item.label}>
              <Text
                fontWeight={fontWeightEnum.Medium}
                variant={textVariantsEnum.Text_13}
              >
                {item.label}
              </Text>
              :
              <Text
                variant={textVariantsEnum.Text_13}
                fontWeight={fontWeightEnum.Normal}
              >
                {item.value}
              </Text>
            </div>
          ))}
        </div>
        <div className={styles.formContainer}>
          <Dropdown
            options={filteredLeaveTypeOptions}
            label="Leave Type"
            value={formData.leaveType}
            selectStyles={borderedSelectStyles}
            disabled={approvedLeave}
            onChange={(option) =>
              setFormData((prev) => ({ ...prev, leaveType: option }))
            }
          />
          <Input
            type="text"
            label="Comment"
            placeholder="Add a comment"
            value={formData.leaveComment}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, leaveComment: e.target.value }))
            }
            disabled={approvedLeave}
          />
          <Dropdown
            label="Manager"
            options={companyManagersDropdownOptions}
            value={formData.leaveManager}
            onChange={(option) =>
              setFormData((prev) => ({ ...prev, leaveManager: option }))
            }
            selectStyles={borderedSelectStyles}
            disabled={approvedLeave}
          />
          <div className={styles.updateLeaveDatePicker}>
            <Text {...commonDateLabelTextProps}>Date</Text>
            <DatePicker
              className={styles.datePickerInput}
              selected={selectedLeaveDate}
              onChange={(date) => {
                if (!date) {
                  setFormData((prev) => ({ ...prev, leaveDate: null }));
                  return;
                }
                if (!canUseLeaveDateSelection(date)) return;
                setFormData((prev) => ({
                  ...prev,
                  leaveDate: {
                    year: date.getFullYear(),
                    month: date.getMonth() + 1,
                    day: date.getDate(),
                  },
                }));
              }}
              placeholderText="Select date"
              dateFormat="yyyy-MM-dd"
              minDate={todayMin}
              filterDate={isLeaveDateSelectableInCalendar}
              disabled={approvedLeave}
              popperClassName={styles.leaveDatePickerPopper}
            />
          </div>
        </div>
      </div>
      {!approvedLeave && (
        <div className={styles.buttonContainer}>
          <Button
            variant={buttonVariants.primary}
            text="Update"
            onClick={handleUpdate}
            loading={isSubmitting}
            disabled={isSubmitting || isDeleting}
          />
          <Button
            variant={buttonVariants.red}
            text="Delete"
            onClick={handleDelete}
            loading={isDeleting}
            disabled={isSubmitting || isDeleting}
          />
        </div>
      )}
    </div>
  );
};
